import { useCallback, useEffect, useMemo, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { components } from "@shared-core/data/api-schema.generated";
import { deliveryFromWire } from "@shared-core/data/catalogue-delivery-wire";
import { deliverySelectionBody } from "@shared-core/data/delivery-selection-body";
import type {
  DeliveryMode,
  DeliverySelection,
} from "@shared-core/data/delivery-types";
import type { CatalogueDeliveryAddress } from "@shared-core/data/api-types";
import type { DeliveryChangePreview } from "@shared-core/data/order-to-invoice-types";
import {
  NO_PICK,
  staffDeliveryChoicesOf,
  type DeliveryPick,
} from "@shared-core/domain/checkout-delivery";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import {
  DeliverySection,
  type DeliveryOpening,
} from "../delivery/DeliverySection";
import { ChangedDelivery } from "../orders/ChangeDeliveryDialog";
import { endpointOf } from "../../patterns/command-shell/CommandShell";
import { isItemToChoose } from "./item-to-choose";
import type {
  CustomWidget,
  CustomWidgetProps,
} from "../../patterns/command-shell/types";

/** The body paths the widget owns: the change's `delivery` selection, and each delivery's choice. */
const PATH = "delivery";
const CHOICES = "choices";

type DeliveryPriceWire = components["schemas"]["DeliveryPriceSchema"];

/** What the dry run tells the widget about the line, beside the body. */
interface DeliveryWidgetContext {
  customer_code: string;
  quantity: number;
  opening: DeliveryOpening[];
  allowed_modes: DeliveryMode[];
  /** The change as priced (once a delivery is chosen): each delivery it touches or makes, with its options. */
  change: DeliveryChangePreview | null;
  /**
   * A new line's delivery as priced (an item's Add to draft quote…), with each split
   * row's: Ship then offers its couriers, as the item page's Delivery section
   * does. Absent for a command that prices no line delivery here.
   */
  priced?: DeliveryPriceWire | null;
  destinations?: DeliveryPriceWire[];
  /** Each route not offered because its delivery isn't priced (order to invoice doc 107), shown with the agreed
   *  price that makes it an option. */
  delivery_not_priced_warnings?: string[];
}

/** The widget's context; `waiting` while the line's customer is still to be chosen (its addresses are the customer's). */
function contextOf(
  props: CustomWidgetProps,
): DeliveryWidgetContext | "waiting" | null {
  const context = props.hints[PATH]?.widget_context as
    Partial<DeliveryWidgetContext> | undefined;
  if (context && context.customer_code === null) return "waiting";
  if (!context?.customer_code || !Array.isArray(context.opening)) return null;
  return {
    customer_code: context.customer_code,
    quantity: Number(context.quantity ?? 0),
    opening: context.opening,
    allowed_modes: context.allowed_modes ?? ["ship", "multi"],
    change: context.change ?? null,
    ...("priced" in context
      ? {
          priced: context.priced ?? null,
          destinations: context.destinations ?? [],
          delivery_not_priced_warnings:
            context.delivery_not_priced_warnings ?? [],
        }
      : {}),
  };
}

/**
 * The delivery-allocation widget (brief §9.3): Ship or Split across places,
 * the line's deliveries as they are with a row goods have gone to locked at
 * its minimum, the customer's addresses, and "Capture a new address…", which
 * runs the create-address command nested in the same shell; then each
 * delivery the change touches or makes as Classic's dialog draws it (the
 * basket's delivery card): its level and courier, collection or an agreed
 * price with its reason, prefilled with the default. Every delivery's choice
 * goes in `choices` with the price shown for it (the command's guard: a price
 * moved since is a 409 stale, and the shell reads the change again). Lifted
 * from *Change delivery…*'s dialog: pricing, refusals and stale answers are
 * the shell's generic dry run.
 */
function DeliveryAllocation(props: CustomWidgetProps) {
  const { errors, disabled, openNested, setValues } = props;
  const dataAdapter = useDataAdapter();
  const index = useObjectManifest();
  const found = contextOf(props);
  const context = found === "waiting" ? null : found;
  const [picks, setPicks] = useState<Record<string, DeliveryPick>>({});
  const change = context?.change ?? null;
  const points = useMemo(() => change?.collection_points ?? [], [change]);
  // The latest answer's deliveries: a regrouped delivery's old pick is dropped, and each is sent with its shown price.
  const choicesOf = useCallback(
    (next: Record<string, DeliveryPick>) => {
      const deliveries = change?.deliveries ?? [];
      const live = Object.fromEntries(
        Object.entries(next).filter(([ref]) =>
          deliveries.some((d) => d.delivery_ref === ref),
        ),
      );
      const shown = deliveries.flatMap((d) => (d.after ? [d.after] : []));
      return staffDeliveryChoicesOf(live, shown, points, "place");
    },
    [change, points],
  );
  const sent = JSON.stringify(props.body[CHOICES] ?? []);
  useEffect(() => {
    if (!change) return;
    const choices = choicesOf(picks);
    if (JSON.stringify(choices) !== sent) setValues({ [CHOICES]: choices });
  }, [change, choicesOf, picks, sent, setValues]);
  const pick = (deliveryRef: string, patch: Partial<DeliveryPick>) => {
    const next = {
      ...picks,
      [deliveryRef]: { ...(picks[deliveryRef] ?? NO_PICK), ...patch },
    };
    setPicks(next);
    setValues({ [CHOICES]: choicesOf(next) });
  };

  const onChange = useCallback(
    (selection: DeliverySelection) =>
      setValues({ [PATH]: deliverySelectionBody(selection) }),
    [setValues],
  );

  // The courier list's delivery, mapped once per answer (the section keeps the last one while a new answer comes).
  const pricedKey =
    context?.priced !== undefined
      ? JSON.stringify([context.priced, context.destinations])
      : null;
  const priced = useMemo(() => {
    if (pricedKey === null) return null;
    const [delivery, rows] = JSON.parse(pricedKey) as [
      DeliveryPriceWire | null,
      DeliveryPriceWire[] | undefined,
    ];
    return {
      pricedDelivery: delivery ? deliveryFromWire(delivery) : null,
      destinationDeliveries: (rows ?? []).map(deliveryFromWire),
    };
  }, [pricedKey]);

  // The nested create comes with the dry run, scoped to this customer's addresses.
  const create = props.hints[PATH]?.create ?? null;
  const captureAddress =
    useCallback(async (): Promise<CatalogueDeliveryAddress | null> => {
      if (!context || !create?.enabled) return null;
      const outcome = await openNested(
        endpointOf(create, index.commandOf(create.name)),
        "A new delivery address for this customer",
      );
      const createdId = outcome?.result.created?.id;
      if (!outcome || !createdId) return null;
      // The command names what it created; the address book gives it back in full.
      const name =
        typeof outcome.body.name === "string" ? outcome.body.name : "";
      const book = await dataAdapter.listCatalogueDeliveryAddresses(
        context.customer_code,
        {
          q: name,
          pageSize: 50,
        },
      );
      return book.addresses.items.find((a) => a.id === createdId) ?? null;
    }, [context, create, dataAdapter, index, openNested]);

  if (found === "waiting") {
    return (
      <p className="text-sm text-muted-foreground">
        The customer's delivery addresses will show once you choose the
        customer.
      </p>
    );
  }
  // Add catalogue item… asks for the line's delivery once its item is chosen: the item's widget says so above.
  if (!context && isItemToChoose(props)) return null;
  if (!context) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Reading the line's deliveries…
      </p>
    );
  }

  const rowErrors: Record<number, string> = {};
  let rowsError: string | null = null;
  let destinationError: string | null = null;
  for (const error of errors) {
    const row = /^delivery\.delivery_lines\.(\d+)\./.exec(error.field);
    if (row) rowErrors[Number(row[1])] = error.message;
    else if (error.field === "delivery.delivery_lines")
      rowsError = error.message;
    else if (
      error.field === "delivery.delivery_address_id" ||
      error.field === PATH
    )
      destinationError = error.message;
  }

  return (
    <div className="space-y-4">
      <DeliverySection
        key={context.customer_code}
        customerCode={context.customer_code}
        addressBook="customer_spine"
        productionSite=""
        allowedModes={context.allowed_modes}
        quantity={context.quantity}
        opening={context.opening}
        {...(priced ?? {})}
        deliveryNotPricedWarnings={context.delivery_not_priced_warnings}
        destinationError={destinationError}
        deliveryLinesError={rowsError}
        deliveryLineErrors={rowErrors}
        onChange={onChange}
        disabled={disabled}
        captureAddress={captureAddress}
      />
      {change && change.deliveries.length > 0 && (
        <section aria-label="Deliveries" className="space-y-3">
          <h3 className="text-sm font-semibold">Deliveries</h3>
          <ul className="space-y-3">
            {change.deliveries.map((d) => (
              <ChangedDelivery
                key={d.delivery_ref}
                delivery={d}
                points={points}
                pick={picks[d.delivery_ref]}
                change={null}
                disabled={disabled}
                onPick={pick}
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export const deliveryAllocationWidget: CustomWidget = {
  ownedPaths: [PATH, CHOICES],
  Component: DeliveryAllocation,
};

export { DeliveryAllocation as DeliveryAllocationWidget };
